import React, { createContext, useContext, useEffect } from 'react';
import { AccessibilityPreferences } from '../types';

interface AccessibilityContextType {
  prefs: AccessibilityPreferences;
  update: (changes: Partial<AccessibilityPreferences>) => void;
}

const DEFAULTS: Required<AccessibilityPreferences> = {
  colorBlindMode: 'none',
  fontScale: 'normal',
  highContrast: false,
  reducedMotion: false,
  dyslexicFont: false,
  enhancedFocus: true,
  lineSpacing: 'normal',
};

const AccessibilityContext = createContext<AccessibilityContextType>({
  prefs: DEFAULTS,
  update: () => {},
});

function applyToDOM(prefs: AccessibilityPreferences) {
  const root = document.documentElement;
  const p = { ...DEFAULTS, ...prefs };

  // Color blind mode
  if (p.colorBlindMode && p.colorBlindMode !== 'none') {
    root.setAttribute('data-colorblind', p.colorBlindMode);
  } else {
    root.removeAttribute('data-colorblind');
  }

  // Font scale
  if (p.fontScale && p.fontScale !== 'normal') {
    root.setAttribute('data-fontscale', p.fontScale);
  } else {
    root.removeAttribute('data-fontscale');
  }

  // Line spacing
  if (p.lineSpacing && p.lineSpacing !== 'normal') {
    root.setAttribute('data-linespacing', p.lineSpacing);
  } else {
    root.removeAttribute('data-linespacing');
  }

  // Boolean class toggles
  root.classList.toggle('high-contrast', !!p.highContrast);
  root.classList.toggle('reduce-motion', !!p.reducedMotion);
  root.classList.toggle('dyslexic-font', !!p.dyslexicFont);
  root.classList.toggle('focus-enhanced', !!p.enhancedFocus);
}

function injectColorBlindFilters() {
  if (document.getElementById('cb-svg-filters')) return;
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.id = 'cb-svg-filters';
  svg.setAttribute('style', 'position:absolute;width:0;height:0;overflow:hidden');
  svg.setAttribute('aria-hidden', 'true');
  svg.innerHTML = `
    <defs>
      <filter id="cb-deuteranopia">
        <feColorMatrix type="matrix" values="
          0.367 0.861 -0.228 0 0
          0.280 0.673  0.047 0 0
         -0.012 0.043  0.969 0 0
          0     0      0     1 0"/>
      </filter>
      <filter id="cb-protanopia">
        <feColorMatrix type="matrix" values="
          0.152 1.053 -0.205 0 0
          0.115 0.786  0.099 0 0
         -0.004 -0.048 1.052 0 0
          0      0     0     1 0"/>
      </filter>
      <filter id="cb-tritanopia">
        <feColorMatrix type="matrix" values="
          1.256 -0.077 -0.180 0 0
         -0.078  0.931  0.148 0 0
          0.005  0.691  0.304 0 0
          0      0      0     1 0"/>
      </filter>
      <filter id="cb-achromatopsia">
        <feColorMatrix type="saturate" values="0"/>
      </filter>
    </defs>`;
  document.body.appendChild(svg);
}

export const AccessibilityProvider: React.FC<{
  children: React.ReactNode;
  prefs: AccessibilityPreferences;
  onUpdate: (changes: Partial<AccessibilityPreferences>) => void;
}> = ({ children, prefs, onUpdate }) => {
  useEffect(() => {
    injectColorBlindFilters();
  }, []);

  useEffect(() => {
    applyToDOM(prefs);
  }, [prefs]);

  return (
    <AccessibilityContext.Provider value={{ prefs: { ...DEFAULTS, ...prefs }, update: onUpdate }}>
      {children}
    </AccessibilityContext.Provider>
  );
};

export const useAccessibility = () => useContext(AccessibilityContext);

/** Call this to sync accessibility prefs loaded from Firestore into the DOM immediately */
export function syncAccessibilityFromProfile(prefs: AccessibilityPreferences | undefined) {
  applyToDOM(prefs ?? {});
}
